import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { focusAfterPaint } from '@/utils/dom';

import { getOfficerLabel } from '@/utils/legacy-utils';

export interface MileageModalProps {
  open: boolean;
  officerName?: string;
  startingMileage?: number | null;
  endingMileage?: number | null;
  theme?: string;
  onSave: (data: { startingMileage: number | null; endingMileage: number | null }) => void;
  onCancel: () => void;
}

export function MileageModal({
  open,
  officerName = '',
  startingMileage = null,
  endingMileage = null,
  theme,
  onSave,
  onCancel,
}: MileageModalProps) {
  const [formStarting, setFormStarting] = useState<number | null>(null);
  const [formEnding, setFormEnding] = useState<number | null>(null);
  const [touched, setTouched] = useState({ starting: false, ending: false });
  const inputRef = useRef<HTMLInputElement>(null);

  const officerLabel = useMemo(() => getOfficerLabel(), []);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormStarting(startingMileage);
      setFormEnding(endingMileage);
      setTouched({ starting: false, ending: false });
      focusAfterPaint(inputRef);
    }
  }, [open, startingMileage, endingMileage]);

  // Computed values
  const difference = useMemo(() => {
    if (formStarting !== null && formEnding !== null) {
      return formEnding - formStarting;
    }
    return null;
  }, [formStarting, formEnding]);

  const isValid = useMemo(() => {
    if (formStarting === null && formEnding === null) return false;
    if (formStarting !== null && formStarting < 0) return false;
    if (formEnding !== null && formEnding < 0) return false;
    if (formStarting !== null && formEnding !== null) {
      if (formEnding < formStarting) return false;
    }
    return true;
  }, [formStarting, formEnding]);

  const showEndingError = useMemo(() => {
    return (
      touched.ending &&
      formEnding !== null &&
      formStarting !== null &&
      formEnding < formStarting
    );
  }, [touched.ending, formEnding, formStarting]);

  const handleSave = useCallback(() => {
    if (!isValid) return;
    onSave({
      startingMileage: formStarting,
      endingMileage: formEnding,
    });
  }, [isValid, formStarting, formEnding, onSave]);

  const handleStartingInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setFormStarting(val === '' ? null : parseFloat(val));
    setTouched((prev) => ({ ...prev, starting: true }));
  }, []);

  const handleEndingInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setFormEnding(val === '' ? null : parseFloat(val));
    setTouched((prev) => ({ ...prev, ending: true }));
  }, []);

  return (
    <BaseFormModal
      open={open}
      title={`Mileage for ${officerName || officerLabel}`}
      theme={theme}
      saveText="Save"
      cancelText="Cancel"
      saveDisabled={!isValid}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div data-kstm-modal>
        <div className="kstm-field">
          <label className="kstm-label">Starting Mileage</label>
          <input
            ref={inputRef}
            type="number"
            className="kstm-input"
            value={formStarting ?? ''}
            onChange={handleStartingInput}
            placeholder=""
            min="0"
            step="0.1"
          />
        </div>

        <div className="kstm-field">
          <label className="kstm-label">Ending Mileage</label>
          <input
            type="number"
            className={`kstm-input${showEndingError ? ' kstm-input--error' : ''}`}
            value={formEnding ?? ''}
            onChange={handleEndingInput}
            placeholder=""
            min="0"
            step="0.1"
          />
          {showEndingError && (
            <div className="kstm-error">
              Ending mileage must be greater than or equal to starting mileage
            </div>
          )}
        </div>

        {difference !== null && (
          <div className="kstm-difference">
            <span className="kstm-difference-label">Distance Traveled:</span>
            <span className="kstm-difference-value">{difference.toFixed(1)} miles</span>
          </div>
        )}
      </div>
    </BaseFormModal>
  );
}

(window as any).MileageModalReact = MileageModal;
